import { createPathComponent } from '@react-leaflet/core';
import L, { PathOptions } from 'leaflet';
import 'leaflet.markercluster';
import React, { Children, ReactNode, useEffect } from 'react';
import { renderToString } from 'react-dom/server';

type EventHandler = (...args: any[]) => void;

interface ClusterProps {
  spiderLegPolylineOptions?: PathOptions;
  removeOutsideVisibleBounds?: boolean;
  chunkedLoading?: boolean;
  iconCreateFunction?: (cluster: any) => any; // Replace 'any' with the actual types if known
}

interface Props extends ClusterProps {
  children?: ReactNode;
  icon?: ReactNode; //any; // Replace 'any' with the actual type if known
  disableClusteringAtZoom?: number | string;
  spiderfyOnMaxZoom?: boolean;
}

const MarkerClusterGroup = createPathComponent(
  (
    {
      children: _c,
      icon = null,
      disableClusteringAtZoom,
      spiderfyOnMaxZoom = false,
      ...props
    }: Props,
    ctx
  ) => {
    const clusterProps = {
      spiderLegPolylineOptions: { pmIgnore: true, weight: 1.5, color: '#0d6efd', opacity: 0.5 },
      removeOutsideVisibleBounds: true,
      chunkedLoading: true,
      iconCreateFunction: function (cluster) {
        var childCount = cluster.getChildCount();

        var c = ' marker-cluster-';
        if (childCount < 10) {
          c += 'small';
        } else if (childCount < 100) {
          c += 'medium';
        } else {
          c += 'large';
        }

        return new L.DivIcon({
          html: icon
            ? renderToString(
                <div
                  style={{ fontSize: '10px' }}
                  className={'d-flex justify-content-center align-items-center'}
                >
                  <span>{childCount}</span>
                  {icon}
                </div>
              )
            : renderToString(
                <div style={{ fontSize: '10px' }}>
                  <span>{childCount}</span>
                </div>
              ),
          className: 'marker-cluster' + c,
          iconSize: new L.Point(40, 40)
        });
      }
    };
    const clusterEvents = {};

    if (disableClusteringAtZoom) {
      clusterProps['disableClusteringAtZoom'] = disableClusteringAtZoom;
    }

    clusterProps['spiderfyOnMaxZoom'] = spiderfyOnMaxZoom;

    // Splitting props and events to different objects
    Object.entries(props).forEach(([propName, prop]) =>
      propName.startsWith('on') ? (clusterEvents[propName] = prop) : (clusterProps[propName] = prop)
    );

    // Creating markerClusterGroup Leaflet element
    // @ts-ignore markerClusterGroup plugin types not available
    let markerClusterGroup = new L.markerClusterGroup(clusterProps);

    useEffect(() => {
      // Cleanup the old markerClusterGroup instance before creating a new one
      markerClusterGroup.clearLayers();

      // @ts-ignore markerClusterGroup plugin types not available
      markerClusterGroup = new L.markerClusterGroup({
        ...clusterProps,
        disableClusteringAtZoom,
        spiderfyOnMaxZoom // Ensure it's updated on prop change
      });

      // Re-add event listeners
      Object.entries(clusterEvents).forEach(([eventAsProp, callback]) => {
        const clusterEvent = `cluster${eventAsProp.substring(2).toLowerCase()}`;
        markerClusterGroup.on(clusterEvent, callback);
      });

      // Re-add the children markers to the cluster group
      Children.forEach(_c, (child) => {
        // Ensure that the child is a valid React element and has a leaflet layer
        if (React.isValidElement(child) && child.props.leaflet && child.props.leaflet.mapLayer) {
          markerClusterGroup.addLayer(child.props.leaflet.mapLayer);
        }
      });

      // Attach to the map context
      if (ctx.map) {
        ctx.map.addLayer(markerClusterGroup);
      }

      return () => {
        if (ctx.map) {
          ctx.map.removeLayer(markerClusterGroup); // Clean up when component unmounts
        }
      };
    }, [disableClusteringAtZoom, spiderfyOnMaxZoom]);

    // markerClusterGroup._getExpandedVisibleBounds = function () {
    //   return markerClusterGroup._map.getBounds();
    // };

    // // Initializing event listeners
    // Object.entries(clusterEvents).forEach(([eventAsProp, callback]) => {
    //   const clusterEvent = `cluster${eventAsProp.substring(2).toLowerCase()}`;
    //   markerClusterGroup.on(clusterEvent, callback);
    // });

    return {
      instance: markerClusterGroup,
      context: { ...ctx, layerContainer: markerClusterGroup }
    };
  }
);

export default MarkerClusterGroup;
